import {
  getAllowedHosts,
  getGripsInternalUrlReplacements,
  getGrpcConfig,
  getHostConfigByHostname_UNSAFE,
  getTadaridaInternalUrlReplacements,
  StaticHostConfig,
} from '@aboutyou/config';
import {ABTests, getWebConfig} from '@aboutyou/feature-flags';
import {generateTranslations, getI18nConfig_UNSAFE} from '@aboutyou/i18n';
import {getRoutesConfig_UNSAFE} from '@aboutyou/router';
import {stripIndent} from 'common-tags';
import type {Request} from 'express';
import {assert} from 'ts-essentials';
import {
  getAppCheckoutAssetsHostOverride,
  isAppCheckoutSubdomainRequest,
} from '../utils/isAppCheckoutSubdomainRequest';
import {StaticConfig} from './types';
// eslint-disable-next-line monorepo/no-internal-import
import {HttpUrl} from '@aboutyou/config/src/urlMappings/types';

interface Config {
  originalRequest: Request;
  hostname: string;
  abTestGroups?: ABTests;
  enableRealUserMode?: boolean;
  traceId: string;
}

export async function createStaticConfig(
  config: Config,
): Promise<StaticConfig> {
  assert(
    !process.browser,
    'Expected createStaticConfig to be called on the server only',
  );
  const {originalRequest, hostname, abTestGroups, enableRealUserMode, traceId} =
    config;
  // eslint-disable-next-line no-restricted-syntax
  const hostConfig = getHostConfigByHostname_UNSAFE(hostname);

  if (!hostConfig) {
    if (process.env.NODE_ENV === 'production') {
      throw new Error('Invalid request');
    }
    const allowedHosts = getAllowedHosts(['development']);
    throw new Error(
      stripIndent`
        Expected to find a matching host config for hostname "${hostname}", but could not find any. For local development, consider using one of the following:
      ` + allowedHosts.join(`\n  `),
    );
  }

  const {locale, alternateLocales: alternateLocalesConfig} = hostConfig;
  const alternateLocales =
    alternateLocalesConfig?.map(({locale}) => locale) ?? [];

  const grpcConfig = getGrpcConfig(locale, hostConfig.device, abTestGroups);

  const url = new URL(originalRequest.url, `https://${hostname}`);
  const search = new URLSearchParams(url.search);

  const isAppCheckout = isAppCheckoutSubdomainRequest(originalRequest);

  // tadaridaBackendApiOverride is blocked for App Checkout requests to prevent
  // reflected XSS: the value is embedded unsanitised in SSR HTML via JSON.stringify,
  // and </script> in JSON is not escaped, allowing script injection (VULN-4035).
  if (
    hostConfig.environment !== 'production' &&
    !isAppCheckout &&
    search.has('tadaridaBackendApiOverride')
  ) {
    hostConfig.tadaridaUrl = search.get(
      'tadaridaBackendApiOverride',
    ) as typeof hostConfig.tadaridaUrl;
  }

  /**
   * Checkout assets url override is needed for App Checkout subdomain requests
   */
  if (isAppCheckout) {
    hostConfig.checkoutFrontendHost = getAppCheckoutAssetsHostOverride(
      originalRequest,
    ) as HttpUrl;

    // app checkout in dev mode should not use production CDN for assets
    if (originalRequest.hostname.includes('.local')) {
      hostConfig.assetsUrl = undefined;
    }
  }

  const serverHostConfig: StaticHostConfig = createServerHostConfig(
    hostConfig,
    originalRequest.url,
    !isAppCheckout,
  );

  const [translations, webConfig] = await Promise.all([
    generateTranslations({
      grpcConfig: {traceId, ...grpcConfig},
      hostConfig: serverHostConfig,
    }),
    getWebConfig({
      searchParams: search,
      grpcConfig: {traceId, ...grpcConfig},
      hostConfig: serverHostConfig,
    }),
  ]);

  const mergedConfig = isAppCheckout
    ? ({} as typeof webConfig.featureFlags.mergedConfig)
    : webConfig.featureFlags.mergedConfig;

  const cdnAssetsDisabled =
    webConfig.featureFlags.mergedConfig.serve_assets_from_origin?.defaultValue;

  if (cdnAssetsDisabled) {
    hostConfig.assetsUrl = undefined;
    serverHostConfig.assetsUrl = undefined;
  }

  return {
    hostConfig,
    serverHostConfig,
    i18nConfig: {
      locale,
      translations: translations ?? {},
      // eslint-disable-next-line no-restricted-syntax
      ...getI18nConfig_UNSAFE(locale),
    },
    grpcConfig,
    // eslint-disable-next-line no-restricted-syntax
    routesConfig: getRoutesConfig_UNSAFE(locale, alternateLocales),
    featureFlagConfig: {
      mergedConfig,
      version: webConfig.featureFlags.version,
    },
    enableRealUserMode: Boolean(enableRealUserMode),
  };
}

export const createServerHostConfig = (
  hostConfig: StaticHostConfig,
  requestUrl: string,
  allowBackendOverride = true,
) => {
  let tadaridaUrl = getTadaridaInternalUrlReplacements(hostConfig.environment);
  const gripsUrl = getGripsInternalUrlReplacements(hostConfig.environment);

  if (hostConfig.environment !== 'production' && allowBackendOverride) {
    const url = new URL(requestUrl, `https://${hostConfig.hostname}`);

    const search = new URLSearchParams(url.search);
    if (search.has('tadaridaBackendApiOverride')) {
      tadaridaUrl = search.get(
        'tadaridaBackendApiOverride',
      ) as typeof hostConfig.tadaridaUrl;
    }
  }

  return {
    ...hostConfig,
    tadaridaUrl,
    gripsUrl,
    legacyConfig: hostConfig.legacyConfig,
  } as StaticHostConfig;
};
